import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";

const TABS = [
    { key: "shipping", it: "Spedizioni", en: "Shipping" },
    { key: "refund", it: "Resi e rimborsi", en: "Returns & refunds" },
    { key: "terms", it: "Condizioni di vendita", en: "Terms of sale" },
    { key: "privacy", it: "Privacy shop", en: "Shop privacy" },
];

/* Shop policies rendered live from Shopify (single source of truth: Shopify admin → Policies). */
export default function ShopInfo() {
    const { lang } = useLang();
    const en = lang === "en";
    const [params, setParams] = useSearchParams();
    const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "shipping";
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        api.get("/shop/policies").then((r) => setData(r.data)).catch(() => setErr(true));
    }, []);

    const pol = data?.[tab];

    return (
        <div data-testid="shop-info-page" className="bg-abnormal-ink min-h-screen">
            <SEO title={en ? "Shipping, returns & terms · Shop ABnormal" : "Spedizioni, resi e condizioni · Shop ABnormal"}
                description={en ? "Shipping, returns and terms of sale for ABnormal Clothing, made to order." : "Spedizioni, resi e condizioni di vendita di ABnormal Clothing, realizzata su ordinazione."}
                path="/shop/info" />
            <PageHeader label="ABNORMAL CLOTHING" title={en ? "SHOP INFO" : "INFO SHOP"}
                sub={en ? "Shipping, returns and terms — the same policies you accept at Shopify checkout." : "Spedizioni, resi e condizioni — le stesse policy che accetti al checkout Shopify."} />
            <div className="max-w-[1100px] mx-auto px-5 md:px-10 pb-24">
                <div role="tablist" className="flex flex-wrap gap-2 mb-8" data-testid="shop-info-tabs">
                    {TABS.map((t) => (
                        <button key={t.key} role="tab" aria-selected={tab === t.key} data-testid={`shop-info-tab-${t.key}`}
                            onClick={() => setParams({ tab: t.key })}
                            className={`label-mono px-3 py-2 border transition-colors ${tab === t.key ? "border-abnormal-acid text-abnormal-acid" : "border-white/15 text-abnormal-paperDim hover:border-white/40 hover:text-abnormal-paper"}`}>
                            {en ? t.en : t.it}
                        </button>
                    ))}
                </div>
                {err && (
                    <p data-testid="shop-info-error" className="text-abnormal-warn text-sm">
                        {en ? "Policies temporarily unavailable. Please retry in a moment." : "Policy momentaneamente non disponibili. Riprova tra poco."}
                    </p>
                )}
                {!err && !data && <div className="h-40 border border-white/10 animate-pulse" />}
                {data && !pol && (
                    <p data-testid="shop-info-missing" className="text-abnormal-paperDim text-sm">
                        {en ? "This policy is not published on Shopify yet." : "Questa policy non è ancora pubblicata su Shopify."}
                    </p>
                )}
                {pol && (
                    <article data-testid={`shop-info-${tab}`} className="bg-abnormal-charcoal border border-white/10 p-6 md:p-10">
                        <div className="prose prose-invert prose-sm md:prose-base max-w-none text-abnormal-paperDim [&_strong]:text-abnormal-paper [&_a]:text-abnormal-acid [&_h1,&_h2,&_h3]:font-display [&_h1,&_h2,&_h3]:uppercase"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pol.body || "", { ADD_ATTR: ["target"] }) }} />
                        <a href={pol.url} target="_blank" rel="noreferrer" data-testid="shop-info-source"
                            className="mt-8 inline-flex items-center gap-1.5 label-mono text-abnormal-paperDim hover:text-abnormal-acid">
                            {en ? "Official version on Shopify" : "Versione ufficiale su Shopify"} <ArrowUpRight size={12} />
                        </a>
                    </article>
                )}
            </div>
        </div>
    );
}
